import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useSeo } from "@/lib/seo";
import AiImagePicker from "@/components/AiImagePicker";
import {
  AUTHORITY_ASSETS,
  LINK_QUALITY_FRAMEWORK,
  ANCHOR_TEXT_STRATEGY,
  OUTREACH_CATEGORIES,
  AUTHORITY_ROADMAP,
  FORBIDDEN_METHODS,
} from "@/data/authorityAssets";

const API = process.env.REACT_APP_BACKEND_URL;

const INTENT_ORDER = ["transactional", "commercial", "informational", "navigational"];
const PRIORITY_ORDER = ["p0", "p1", "p2"];
const INTENT_COLOR = {
  transactional: "#a97a2a",
  commercial: "#8a7040",
  informational: "#6b7280",
  navigational: "#5a5a5a",
};
const PRIORITY_COLOR = { p0: "#a44", p1: "#a97a2a", p2: "#6b7280" };

/**
 * /admin/seo — Admin-gated SEO dashboard with three tabs.
 *
 *   1. Keywords       → 381 keywords from /app/memory/seo/keyword_map.json
 *                       merged with Search Console metrics when configured.
 *   2. GSC status     → Google Search Console connection info.
 *   3. Competitors    → Content-type gap analysis from
 *                       /app/memory/seo/competitor_gap.json.
 *
 * NEVER fabricates ranking, traffic, or backlink data. When GSC is not
 * configured, Position / Clicks / Impressions / CTR render as "—".
 */
const AdminSeo = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("keywords");

  useSeo({
    title: "SEO · Admin · Asia Sourcing",
    path: "/admin/seo",
    noindex: true,
  });

  return (
    <main
      style={{ background: "var(--paper)", minHeight: "100svh" }}
      data-testid="admin-seo-page"
    >
      <header
        style={{
          background: "var(--ink)",
          color: "var(--paper)",
          padding: "1.4rem 1.6rem 1.2rem",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <p className="mono" style={{ color: "var(--brass)", letterSpacing: ".12em", marginBottom: ".3rem" }}>
              Asia Sourcing · Admin · SEO
            </p>
            <h1 className="display" style={{ fontSize: "1.6rem", margin: 0, letterSpacing: "-.01em" }}>
              SEO command centre
            </h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span className="mono" style={{ color: "rgba(239,234,224,.62)", fontSize: ".68rem" }} data-testid="admin-seo-user-email">
              {user?.email}
            </span>
            <Link to="/admin/leads" className="btn btn-ghost" style={{ borderColor: "rgba(239,234,224,.2)", color: "rgba(239,234,224,.8)" }}>
              ← Leads
            </Link>
            <Link to="/admin/ai-image-studio" className="btn btn-ghost" style={{ borderColor: "rgba(239,234,224,.2)", color: "rgba(239,234,224,.8)" }} data-testid="admin-seo-nav-ai-studio">
              AI Studio
            </Link>
            <Link to="/" className="btn btn-ghost" style={{ borderColor: "rgba(239,234,224,.2)", color: "rgba(239,234,224,.8)" }}>
              Site
            </Link>
            <button type="button" onClick={logout} className="btn btn-ghost" style={{ borderColor: "rgba(239,234,224,.2)", color: "rgba(239,234,224,.8)" }} data-testid="admin-seo-logout">
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav
        style={{
          display: "flex",
          gap: 0,
          borderBottom: "1px solid var(--line)",
          background: "var(--paper-raised)",
          padding: "0 1.6rem",
        }}
        role="tablist"
        aria-label="SEO dashboard tabs"
      >
        {[
          { id: "keywords", label: "Keywords + GSC" },
          { id: "insights", label: "Search insights (per page)" },
          { id: "opportunities", label: "Zero-click opportunities" },
          { id: "freshness", label: "Content freshness" },
          { id: "gsc", label: "GSC connection" },
          { id: "competitors", label: "Competitor gap" },
          { id: "authority", label: "Authority assets" },
          { id: "drafts", label: "Page drafts (hero images)" },
          { id: "resend", label: "Resend status" },
        ].map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-seo-tab-${t.id}`}
            style={{
              background: "transparent",
              border: 0,
              padding: "1rem 1.4rem",
              cursor: "pointer",
              fontFamily: "var(--mono)",
              fontSize: ".72rem",
              letterSpacing: ".1em",
              textTransform: "uppercase",
              color: tab === t.id ? "var(--ink)" : "var(--ink-soft)",
              borderBottom: tab === t.id ? "2px solid var(--brass-deep)" : "2px solid transparent",
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "keywords" && <KeywordsTab />}
      {tab === "insights" && <InsightsTab />}
      {tab === "opportunities" && <OpportunitiesTab />}
      {tab === "freshness" && <FreshnessTab />}
      {tab === "gsc" && <GscTab />}
      {tab === "competitors" && <CompetitorsTab />}
      {tab === "authority" && <AuthorityTab />}
      {tab === "drafts" && <PageDraftsTab />}
      {tab === "resend" && <ResendStatusTab />}
    </main>
  );
};

// ─────────────────────────────────────────────────────────────
// Keywords tab — keyword map merged with GSC metrics
// ─────────────────────────────────────────────────────────────
const KeywordsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [intent, setIntent] = useState("all");
  const [priority, setPriority] = useState("all");
  const [sortKey, setSortKey] = useState("priority");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        // Prefer merged GSC endpoint; fallback to plain keyword endpoint on 502.
        let r = await fetch(`${API}/api/admin/seo/gsc/keywords`, { credentials: "include" });
        if (r.status === 502) {
          r = await fetch(`${API}/api/admin/seo/keywords`, { credentials: "include" });
        }
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        setData(await r.json());
      } catch (e) {
        setErr(e.message || "Could not load keyword data.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const keywords = useMemo(() => data?.keywords || [], [data]);
  const configured = data?.configured === true;

  const filtered = useMemo(() => {
    let list = keywords;
    if (intent !== "all") list = list.filter((k) => k.intent === intent);
    if (priority !== "all") list = list.filter((k) => k.priority === priority);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((k) => k.keyword.toLowerCase().includes(needle) || (k.target_url || "").toLowerCase().includes(needle));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      if (sortKey === "priority") return (PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority)) * dir;
      if (sortKey === "intent") return (INTENT_ORDER.indexOf(a.intent) - INTENT_ORDER.indexOf(b.intent)) * dir;
      if (sortKey === "clicks") return ((a.metrics?.clicks || 0) - (b.metrics?.clicks || 0)) * dir;
      if (sortKey === "impressions") return ((a.metrics?.impressions || 0) - (b.metrics?.impressions || 0)) * dir;
      if (sortKey === "position") {
        const av = a.metrics?.position ?? 999;
        const bv = b.metrics?.position ?? 999;
        return (av - bv) * dir;
      }
      const va = String(a[sortKey] || "").toLowerCase();
      const vb = String(b[sortKey] || "").toLowerCase();
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return list;
  }, [keywords, intent, priority, q, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const counts = useMemo(() => {
    const byIntent = {}, byPriority = {};
    for (const k of keywords) {
      byIntent[k.intent] = (byIntent[k.intent] || 0) + 1;
      byPriority[k.priority] = (byPriority[k.priority] || 0) + 1;
    }
    let totalClicks = 0, totalImpressions = 0, ranked = 0;
    if (configured) {
      for (const k of keywords) {
        totalClicks += k.metrics?.clicks || 0;
        totalImpressions += k.metrics?.impressions || 0;
        if (k.metrics?.position != null) ranked++;
      }
    }
    return { byIntent, byPriority, totalClicks, totalImpressions, ranked };
  }, [keywords, configured]);

  return (
    <>
      {/* Connection banner */}
      <section
        style={{
          padding: "1rem 1.6rem",
          background: configured ? "rgba(83,110,64,.08)" : "rgba(197,168,128,.08)",
          borderBottom: "1px solid var(--line)",
        }}
        data-testid="admin-seo-gsc-notice"
      >
        <p className="mono" style={{ fontSize: ".72rem", letterSpacing: ".08em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .3rem" }}>
          Google Search Console · {configured ? "connected" : "not connected"}
        </p>
        <p style={{ margin: 0, color: "var(--ink)", fontSize: ".92rem", lineHeight: 1.5 }}>
          {configured
            ? `Live ranking data for ${data.site_url} · Last 28 days (${data.period_start} → ${data.period_end}). Data cached for 6 hours; refresh via the GSC tab.`
            : "Search Console is not connected. Position, Impressions, Clicks and CTR will appear here once you finish the setup on the GSC connection tab — the API is wired up and waiting for env-var configuration. Until then this dashboard shows the canonical keyword→URL map only, with no fabricated metrics."}
        </p>
      </section>

      {/* Summary tiles */}
      {!loading && !err && (
        <section
          style={{ padding: "1.2rem 1.6rem", borderBottom: "1px solid var(--line)", background: "var(--paper-raised)", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem" }}
          data-testid="admin-seo-summary"
        >
          <SummaryTile label="Total keywords" value={keywords.length} />
          <SummaryTile label="P0 · money kw" value={counts.byPriority.p0 || 0} accent="#a44" />
          <SummaryTile label="P1 · mid funnel" value={counts.byPriority.p1 || 0} accent="#a97a2a" />
          <SummaryTile label="P2 · long tail" value={counts.byPriority.p2 || 0} accent="#6b7280" />
          {configured ? (
            <>
              <SummaryTile label="Ranking (has position)" value={counts.ranked} accent="#556e40" />
              <SummaryTile label="28-day clicks" value={counts.totalClicks} accent="#556e40" />
              <SummaryTile label="28-day impressions" value={counts.totalImpressions} accent="#556e40" />
              <SummaryTile
                label="28-day CTR"
                value={counts.totalImpressions ? `${((counts.totalClicks / counts.totalImpressions) * 100).toFixed(2)}%` : "—"}
                accent="#556e40"
              />
            </>
          ) : (
            <>
              <SummaryTile label="Transactional" value={counts.byIntent.transactional || 0} />
              <SummaryTile label="Commercial" value={counts.byIntent.commercial || 0} />
              <SummaryTile label="Informational" value={counts.byIntent.informational || 0} />
              <SummaryTile label="Navigational" value={counts.byIntent.navigational || 0} />
            </>
          )}
        </section>
      )}

      {/* Filters */}
      <section style={{ padding: "1.2rem 1.6rem", borderBottom: "1px solid var(--line)", display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
        <input type="search" placeholder="Search keyword or URL…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-seo-search" style={inputStyle} />
        <select value={intent} onChange={(e) => setIntent(e.target.value)} data-testid="admin-seo-filter-intent" style={filterSelectStyle}>
          <option value="all">All intents</option>
          {INTENT_ORDER.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="admin-seo-filter-priority" style={filterSelectStyle}>
          <option value="all">All priorities</option>
          {PRIORITY_ORDER.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <span className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".08em" }} data-testid="admin-seo-visible-count">
          {filtered.length} visible
        </span>
      </section>

      {/* Table */}
      <section style={{ padding: "1.5rem 1.6rem" }}>
        {loading && <p className="mono" style={{ color: "var(--brass-deep)" }} data-testid="admin-seo-loading">Loading keyword map…</p>}
        {err && <p className="mono" style={errBoxStyle} data-testid="admin-seo-error">Could not load keyword data: {err}</p>}
        {!loading && !err && filtered.length === 0 && <p style={{ color: "var(--ink-soft)", fontStyle: "italic" }} data-testid="admin-seo-empty">No keywords match the current filter.</p>}
        {!loading && !err && filtered.length > 0 && (
          <div style={{ overflowX: "auto", border: "1px solid var(--line)", borderRadius: 2 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".88rem" }} data-testid="admin-seo-table">
              <thead>
                <tr>
                  <ThSortable label="Keyword" k="keyword" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <ThSortable label="Intent" k="intent" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <ThSortable label="Priority" k="priority" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <ThSortable label="Target URL" k="target_url" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <ThSortable label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <ThSortable label="Position" k="position" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <ThSortable label="Impressions" k="impressions" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <ThSortable label="Clicks" k="clicks" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <th style={ThStyle}>CTR</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((k, i) => (
                  <tr key={`${k.keyword}-${i}`} data-testid={`admin-seo-row-${i}`}>
                    <td style={TdStyle}><b>{k.keyword}</b></td>
                    <td style={TdStyle}><span className="mono" style={{ color: INTENT_COLOR[k.intent] || "var(--ink)", ...tinyMonoStyle }}>{k.intent}</span></td>
                    <td style={TdStyle}><span className="mono" style={{ color: PRIORITY_COLOR[k.priority] || "var(--ink)", ...tinyMonoStyle, fontWeight: 600 }}>{k.priority}</span></td>
                    <td style={TdStyle}><a href={k.target_url} target="_blank" rel="noreferrer" style={{ color: "var(--brass-deep)" }}>{k.target_url}</a></td>
                    <td style={TdStyle}><span className="mono" style={{ ...tinyMonoStyle, color: k.status === "primary" ? "var(--ink)" : "var(--ink-soft)" }}>{k.status}</span></td>
                    <td style={metricTd(k.metrics?.position != null)}>{k.metrics?.position != null ? k.metrics.position.toFixed(1) : "—"}</td>
                    <td style={metricTd(k.metrics?.impressions > 0)}>{k.metrics?.impressions > 0 ? k.metrics.impressions.toLocaleString() : "—"}</td>
                    <td style={metricTd(k.metrics?.clicks > 0)}>{k.metrics?.clicks > 0 ? k.metrics.clicks.toLocaleString() : "—"}</td>
                    <td style={metricTd(k.metrics?.ctr_pct > 0)}>{k.metrics?.ctr_pct > 0 ? `${k.metrics.ctr_pct.toFixed(2)}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Search insights (per page) — page-centric GSC view
// ─────────────────────────────────────────────────────────────
const InsightsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selectedPath, setSelectedPath] = useState(null);
  const [queryFilter, setQueryFilter] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true); setErr("");
      try {
        const r = await fetch(`${API}/api/admin/seo/gsc/insights?top_queries=25`, { credentials: "include" });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        const d = await r.json();
        setData(d);
        if (d.pages?.length) setSelectedPath(d.pages[0].path);
      } catch (e) { setErr(String(e.message || e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const selectedPage = data?.pages?.find((p) => p.path === selectedPath);
  const filteredQueries = selectedPage?.top_queries?.filter((q) =>
    !queryFilter || q.query.toLowerCase().includes(queryFilter.toLowerCase())
  ) || [];

  return (
    <section style={{ padding: "1.6rem", display: "grid", gridTemplateColumns: "340px 1fr", gap: "1.4rem" }} data-testid="admin-seo-insights-tab">
      <aside>
        <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .6rem" }}>Pages ({data?.pages?.length || 0})</p>
        {data?.mock && (
          <div style={{ padding: ".5rem .7rem", background: "rgba(197,168,128,.18)", border: "1px solid rgba(169,122,42,.4)", fontSize: ".72rem", color: "#5A4020", marginBottom: ".6rem", lineHeight: 1.45 }} data-testid="admin-seo-insights-mock-banner">
            <b>MOCK DATA</b> — representative preview. Connect GSC to see real metrics for all pages.
          </div>
        )}
        {loading && <p className="mono" style={{ color: "var(--brass-deep)" }}>Loading…</p>}
        {err && <p className="mono" style={errBoxStyle}>{err}</p>}
        {data && (
          <ul style={{ listStyle: "none", padding: 0, margin: 0, maxHeight: "70vh", overflowY: "auto", border: "1px solid var(--line)" }}>
            {data.pages.map((p) => (
              <li key={p.path}>
                <button
                  type="button"
                  onClick={() => { setSelectedPath(p.path); setQueryFilter(""); }}
                  data-testid={`admin-seo-insights-page-${p.path.replace(/\W+/g, "-")}`}
                  style={{
                    width: "100%", textAlign: "left", padding: ".55rem .8rem",
                    background: selectedPath === p.path ? "var(--paper-raised)" : "transparent",
                    border: 0, borderBottom: "1px solid var(--line)", cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  <div style={{ fontSize: ".82rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--ink)" }}>{p.path}</div>
                  <div className="mono" style={{ fontSize: ".62rem", color: "var(--ink-soft)", letterSpacing: ".05em", marginTop: ".2rem" }}>
                    {p.impressions.toLocaleString()} impr · {p.clicks.toLocaleString()} clicks · {p.ctr_pct}% CTR
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <div>
        {!selectedPage && !loading && (
          <div style={{ padding: "2rem", border: "1px dashed var(--line)", color: "var(--ink-soft)", fontStyle: "italic" }}>
            Select a page on the left to see its top queries.
          </div>
        )}
        {selectedPage && (
          <div data-testid="admin-seo-insights-detail">
            <div style={{ marginBottom: ".8rem" }}>
              <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: 0 }}>
                {data.configured ? "Last 28 days" : "Mock · 28-day window"} · {data.period_start} → {data.period_end}
              </p>
              <h3 style={{ fontFamily: "var(--display)", fontSize: "1.3rem", margin: ".2rem 0 0" }}>{selectedPage.path}</h3>
              <div style={{ display: "flex", gap: "1.4rem", marginTop: ".6rem", flexWrap: "wrap" }}>
                <Metric label="Impressions" value={selectedPage.impressions.toLocaleString()} testid="metric-impressions" />
                <Metric label="Clicks" value={selectedPage.clicks.toLocaleString()} testid="metric-clicks" />
                <Metric label="CTR" value={`${selectedPage.ctr_pct}%`} testid="metric-ctr" />
                <Metric label="Avg position" value={selectedPage.position != null ? selectedPage.position : "—"} testid="metric-position" />
              </div>
            </div>

            <div style={{ marginBottom: ".6rem" }}>
              <input
                type="text"
                placeholder="Filter queries…"
                value={queryFilter}
                onChange={(e) => setQueryFilter(e.target.value)}
                data-testid="admin-seo-insights-filter"
                style={{ ...inputStyle, maxWidth: 320 }}
              />
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".88rem" }} data-testid="admin-seo-insights-table">
              <thead>
                <tr>
                  <th style={ThStyle}>Query</th>
                  <th style={{ ...ThStyle, textAlign: "right" }}>Impr</th>
                  <th style={{ ...ThStyle, textAlign: "right" }}>Clicks</th>
                  <th style={{ ...ThStyle, textAlign: "right" }}>CTR</th>
                  <th style={{ ...ThStyle, textAlign: "right" }}>Position</th>
                </tr>
              </thead>
              <tbody>
                {filteredQueries.map((q, i) => (
                  <tr key={i} data-testid={`admin-seo-insights-row-${i}`}>
                    <td style={TdStyle}>{q.query}</td>
                    <td style={{ ...TdStyle, textAlign: "right" }}>{q.impressions.toLocaleString()}</td>
                    <td style={{ ...TdStyle, textAlign: "right" }}>{q.clicks.toLocaleString()}</td>
                    <td style={{ ...TdStyle, textAlign: "right" }}>{q.ctr_pct}%</td>
                    <td style={{ ...TdStyle, textAlign: "right" }}>{q.position}</td>
                  </tr>
                ))}
                {filteredQueries.length === 0 && (
                  <tr><td colSpan={5} style={{ ...TdStyle, textAlign: "center", color: "var(--ink-soft)", fontStyle: "italic" }}>No queries match that filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

const Metric = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: 0 }}>{label}</p>
    <p style={{ fontFamily: "var(--display)", fontSize: "1.4rem", margin: ".1rem 0 0", color: "var(--ink)" }}>{value}</p>
  </div>
);


// ─────────────────────────────────────────────────────────────
// Opportunities tab — queries with impressions but zero clicks
// ─────────────────────────────────────────────────────────────
const OpportunitiesTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [minImpr, setMinImpr] = useState(20);

  const load = async (threshold) => {
    setLoading(true); setErr("");
    try {
      const r = await fetch(`${API}/api/admin/seo/gsc/opportunities?min_impressions=${threshold}&limit=50`, { credentials: "include" });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      setData(await r.json());
    } catch (e) { setErr(String(e.message || e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(minImpr); }, []); // eslint-disable-line

  return (
    <section style={{ padding: "2rem 1.6rem", maxWidth: 1180 }} data-testid="admin-seo-opportunities-tab">
      <h2 className="display" style={{ fontSize: "1.4rem", margin: "0 0 .4rem", letterSpacing: "-.01em" }}>Zero-click opportunities</h2>
      <p style={{ color: "var(--ink-soft)", marginBottom: "1rem", lineHeight: 1.55 }}>
        Queries driving impressions but no clicks — classic signals that a different page should be ranking, that the title/snippet isn't earning the click, or that a dedicated landing page is missing.
      </p>
      {data?.mock && (
        <div style={{ padding: ".6rem .9rem", background: "rgba(197,168,128,.18)", border: "1px solid rgba(169,122,42,.4)", fontSize: ".82rem", color: "#5A4020", marginBottom: "1rem" }} data-testid="admin-seo-opp-mock-banner">
          <b>MOCK DATA</b> — representative preview until GSC is connected.
        </div>
      )}
      <div style={{ display: "flex", gap: ".8rem", alignItems: "center", marginBottom: "1rem" }}>
        <label className="mono" style={{ fontSize: ".72rem", letterSpacing: ".12em", textTransform: "uppercase", color: "var(--brass-deep)" }}>Min impressions</label>
        <input type="number" min={5} value={minImpr} onChange={(e) => setMinImpr(Number(e.target.value) || 20)} style={{ ...inputStyle, maxWidth: 100 }} data-testid="admin-seo-opp-min-impr" />
        <button type="button" onClick={() => load(minImpr)} className="btn btn-ghost" data-testid="admin-seo-opp-apply" style={{ padding: ".4rem 1rem", fontSize: ".82rem" }}>Apply</button>
      </div>
      {loading && <p className="mono" style={{ color: "var(--brass-deep)" }}>Loading…</p>}
      {err && <p className="mono" style={errBoxStyle}>{err}</p>}
      {data && (
        <>
          <p className="mono" style={{ fontSize: ".72rem", color: "var(--ink-soft)", letterSpacing: ".06em", marginBottom: ".4rem" }}>
            {data.opportunities.length} opportunities · period {data.period_start} → {data.period_end}
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".88rem" }} data-testid="admin-seo-opp-table">
            <thead>
              <tr>
                <th style={ThStyle}>Query</th>
                <th style={{ ...ThStyle, textAlign: "right" }}>Impressions</th>
                <th style={{ ...ThStyle, textAlign: "right" }}>Position</th>
                <th style={ThStyle}>Currently on</th>
                <th style={ThStyle}>Recommended page</th>
              </tr>
            </thead>
            <tbody>
              {data.opportunities.map((o, i) => (
                <tr key={i} data-testid={`admin-seo-opp-row-${i}`}>
                  <td style={{ ...TdStyle, fontWeight: 600 }}>{o.query}</td>
                  <td style={{ ...TdStyle, textAlign: "right" }}>{o.impressions.toLocaleString()}</td>
                  <td style={{ ...TdStyle, textAlign: "right" }}>{o.position}</td>
                  <td style={TdStyle}><code style={codeStyle}>{o.currently_ranking_on}</code></td>
                  <td style={TdStyle}>
                    <code style={codeStyle}>{o.recommended_page?.path}</code>
                    {o.recommended_page?.match_score > 0 && (
                      <span className="mono" style={{ fontSize: ".66rem", color: "var(--ink-soft)", marginLeft: ".4rem" }}>match {o.recommended_page.match_score}</span>
                    )}
                  </td>
                </tr>
              ))}
              {data.opportunities.length === 0 && (
                <tr><td colSpan={5} style={{ ...TdStyle, textAlign: "center", color: "var(--ink-soft)", fontStyle: "italic" }}>No zero-click queries above the current threshold.</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Content freshness pulse — flag stale pages
// ─────────────────────────────────────────────────────────────
const FreshnessTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [threshold, setThreshold] = useState(90);
  const [onlyStale, setOnlyStale] = useState(true);

  const load = async (t) => {
    setLoading(true); setErr("");
    try {
      const r = await fetch(`${API}/api/admin/seo/freshness?threshold_days=${t}`, { credentials: "include" });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      setData(await r.json());
    } catch (e) { setErr(String(e.message || e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(threshold); }, []); // eslint-disable-line

  const visible = (data?.pages || []).filter((p) => (onlyStale ? p.is_stale : true));

  return (
    <section style={{ padding: "2rem 1.6rem", maxWidth: 1180 }} data-testid="admin-seo-freshness-tab">
      <h2 className="display" style={{ fontSize: "1.4rem", margin: "0 0 .4rem", letterSpacing: "-.01em" }}>Content freshness pulse</h2>
      <p style={{ color: "var(--ink-soft)", marginBottom: "1rem", lineHeight: 1.55 }}>
        Pages whose underlying data file (or saved hero draft) hasn't been touched for longer than the threshold. Google rewards freshness on commercial-intent pages — a stale page quietly loses rank even when nothing is broken.
      </p>
      <div style={{ display: "flex", gap: ".8rem", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap" }}>
        <label className="mono" style={{ fontSize: ".72rem", letterSpacing: ".12em", textTransform: "uppercase", color: "var(--brass-deep)" }}>Threshold (days)</label>
        <input type="number" min={1} value={threshold} onChange={(e) => setThreshold(Number(e.target.value) || 90)} style={{ ...inputStyle, maxWidth: 100 }} data-testid="admin-seo-fresh-threshold" />
        <button type="button" onClick={() => load(threshold)} className="btn btn-ghost" data-testid="admin-seo-fresh-apply" style={{ padding: ".4rem 1rem", fontSize: ".82rem" }}>Apply</button>
        <label style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", marginLeft: ".8rem", fontSize: ".82rem", color: "var(--ink)" }}>
          <input type="checkbox" checked={onlyStale} onChange={(e) => setOnlyStale(e.target.checked)} data-testid="admin-seo-fresh-only-stale" />
          Only show stale pages
        </label>
      </div>
      {loading && <p className="mono" style={{ color: "var(--brass-deep)" }}>Loading…</p>}
      {err && <p className="mono" style={errBoxStyle}>{err}</p>}
      {data && (
        <>
          <div style={{ display: "flex", gap: "2rem", marginBottom: "1rem", flexWrap: "wrap" }}>
            <Metric label="Total pages" value={data.total_pages.toLocaleString()} testid="fresh-total" />
            <Metric label="Stale (≥ threshold)" value={data.stale_count.toLocaleString()} testid="fresh-stale" />
            <Metric label="Showing" value={visible.length.toLocaleString()} testid="fresh-visible" />
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".88rem" }} data-testid="admin-seo-fresh-table">
            <thead>
              <tr>
                <th style={ThStyle}>Path</th>
                <th style={{ ...ThStyle, textAlign: "right" }}>Age (days)</th>
                <th style={ThStyle}>Last touched</th>
                <th style={ThStyle}>Source</th>
                <th style={ThStyle}>Stale?</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p, i) => (
                <tr key={p.path} data-testid={`admin-seo-fresh-row-${i}`}>
                  <td style={TdStyle}><code style={codeStyle}>{p.path}</code></td>
                  <td style={{ ...TdStyle, textAlign: "right", color: p.is_stale ? "#b23" : "var(--ink)" }}>{p.age_days ?? "—"}</td>
                  <td style={TdStyle}>{p.last_touched ? new Date(p.last_touched).toLocaleDateString() : "—"}</td>
                  <td style={{ ...TdStyle, fontSize: ".76rem", color: "var(--ink-soft)" }}>{p.source}</td>
                  <td style={{ ...TdStyle, color: p.is_stale ? "#b23" : "#3a7", fontWeight: 600 }}>{p.is_stale ? "STALE" : "fresh"}</td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr><td colSpan={5} style={{ ...TdStyle, textAlign: "center", color: "var(--ink-soft)", fontStyle: "italic" }}>Nothing matches the current filter.</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
};


// ─────────────────────────────────────────────────────────────
// GSC connection tab — status + setup instructions
// ─────────────────────────────────────────────────────────────
const GscTab = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/seo/gsc/status`, { credentials: "include" });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        setStatus(await r.json());
      } catch (e) {
        setErr(e.message || "Could not load GSC status.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section style={{ padding: "2rem 1.6rem", maxWidth: 820 }} data-testid="admin-seo-gsc-panel">
      <h2 className="display" style={{ fontSize: "1.4rem", margin: "0 0 1rem", letterSpacing: "-.01em" }}>
        Google Search Console
      </h2>
      {loading && <p className="mono" style={{ color: "var(--brass-deep)" }}>Loading connection status…</p>}
      {err && <p className="mono" style={errBoxStyle}>{err}</p>}
      {status && (
        <>
          <div
            style={{
              border: "1px solid var(--line)",
              padding: "1.2rem 1.4rem",
              background: status.configured ? "rgba(83,110,64,.08)" : "rgba(197,168,128,.08)",
              marginBottom: "1.4rem",
            }}
            data-testid="admin-seo-gsc-status-card"
          >
            <p className="mono" style={{ fontSize: ".7rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .4rem" }}>
              Status · {status.configured ? "connected" : "not connected"}
            </p>
            <p style={{ margin: 0, color: "var(--ink)", lineHeight: 1.55 }}>
              {status.configured ? (
                <>Property <b>{status.site_url}</b> is connected. Ranking data appears on the Keywords tab and refreshes every 6 hours.</>
              ) : (
                <>Search Console is not configured yet. Follow the steps below to connect a property. Position, Impressions, Clicks and CTR will appear on the Keywords tab immediately after configuration.</>
              )}
            </p>
          </div>
          {!status.configured && (
            <>
              <h3 style={{ fontFamily: "var(--display)", fontSize: "1.05rem", margin: "1.5rem 0 .5rem" }}>Setup — one-time, ~10 minutes</h3>
              <ol style={{ color: "var(--ink)", lineHeight: 1.65, paddingLeft: "1.2rem" }}>
                <li style={{ marginBottom: ".5rem" }}>
                  In Google Cloud Console, create a project and enable the <b>Search Console API</b> (console.cloud.google.com → APIs &amp; Services → Library → Search Console API).
                </li>
                <li style={{ marginBottom: ".5rem" }}>
                  Create a <b>service account</b> under IAM &amp; Admin → Service Accounts. Generate a JSON key for the service account and download it.
                </li>
                <li style={{ marginBottom: ".5rem" }}>
                  In Search Console (search.google.com/search-console), open the property, go to <b>Settings → Users and permissions</b>, and add the service-account email as a user with <b>Restricted</b> permission.
                </li>
                <li style={{ marginBottom: ".5rem" }}>
                  Copy the service-account JSON to the backend server at a stable path, for example <code style={codeStyle}>/app/backend/secrets/gsc-service-account.json</code>.
                </li>
                <li style={{ marginBottom: ".5rem" }}>
                  In <code style={codeStyle}>/app/backend/.env</code>, set:<br />
                  <code style={{ ...codeStyle, display: "block", marginTop: ".4rem" }}>
                    GSC_SERVICE_ACCOUNT_JSON=/app/backend/secrets/gsc-service-account.json{"\n"}
                    GSC_SITE_URL=sc-domain:asiasourcing.co.in
                  </code>
                </li>
                <li style={{ marginBottom: ".5rem" }}>
                  Restart the backend: <code style={codeStyle}>sudo supervisorctl restart backend</code>.
                </li>
                <li>Reload this page — the status card should flip to &quot;connected&quot; and real metrics appear on the Keywords tab.</li>
              </ol>
              <p style={{ color: "var(--ink-soft)", fontSize: ".9rem", marginTop: "1.2rem", lineHeight: 1.6 }}>
                Notes: use <code style={codeStyle}>sc-domain:asiasourcing.co.in</code> for a Domain property, or the full URL for a URL-prefix property.
                The service-account JSON file must be readable by the backend process only — never commit it to git and never expose it via a static route.
                The GSC API has a quota of 25,000 requests / day / project, well above what this dashboard requires.
              </p>
            </>
          )}
        </>
      )}
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Competitors tab — content-type gap analysis (never fabricated)
// ─────────────────────────────────────────────────────────────
const CompetitorsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/seo/competitors`, { credentials: "include" });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        setData(await r.json());
      } catch (e) {
        setErr(e.message || "Could not load competitor gap.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section style={{ padding: "2rem 1.6rem", maxWidth: 1200 }} data-testid="admin-seo-competitor-panel">
      <h2 className="display" style={{ fontSize: "1.4rem", margin: "0 0 .8rem", letterSpacing: "-.01em" }}>
        Competitor gap analysis
      </h2>
      <p style={{ color: "var(--ink-soft)", marginBottom: "1.4rem", lineHeight: 1.5, maxWidth: "70ch" }}>
        Content-type gap analysis across 10 India-sourcing competitor domains. Uses only publicly-verifiable content-type presence — no fabricated ranking or traffic data. Extend the schema with paid Ahrefs / SEMrush data when subscribed.
      </p>
      {loading && <p className="mono" style={{ color: "var(--brass-deep)" }}>Loading competitor set…</p>}
      {err && <p className="mono" style={errBoxStyle}>{err}</p>}
      {data && (
        <>
          {/* Our baseline */}
          <div style={{ border: "1px solid var(--line)", padding: "1.2rem 1.4rem", background: "rgba(83,110,64,.06)", marginBottom: "1.4rem" }} data-testid="admin-seo-baseline">
            <p className="mono" style={{ fontSize: ".68rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: 0 }}>
              Our baseline · {data.our_baseline.domain}
            </p>
            <p style={{ margin: ".4rem 0 .8rem", color: "var(--ink)" }}>
              <b>{data.our_baseline.buyer_guides_count}</b> buyer guides · <b>{data.our_baseline.cluster_pages_count}</b> cluster pages · <b>{data.our_baseline.services_pages_count}</b> service pages · <b>{data.our_baseline.case_studies_count}</b> case studies · <b>{data.our_baseline.us_commercial_pages_count}</b> US commercial pages
            </p>
            <p style={{ margin: 0, fontSize: ".88rem", color: "var(--ink-soft)" }}>
              Content-types present: {(data.our_baseline.content_types_present || []).join(" · ")}
              {data.our_baseline.content_types_absent?.length > 0 && (
                <> · <b style={{ color: "#a44" }}>gap:</b> {data.our_baseline.content_types_absent.join(", ")}</>
              )}
            </p>
          </div>

          {/* Competitors table */}
          <div style={{ overflowX: "auto", border: "1px solid var(--line)", borderRadius: 2, marginBottom: "1.4rem" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }} data-testid="admin-seo-competitors-table">
              <thead>
                <tr>
                  <th style={ThStyle}>Competitor</th>
                  <th style={ThStyle}>Positioning</th>
                  <th style={ThStyle}>Content-types present</th>
                  <th style={ThStyle}>Topical strength</th>
                  <th style={ThStyle}>Gaps vs us</th>
                </tr>
              </thead>
              <tbody>
                {(data.competitors || []).map((c, i) => (
                  <tr key={c.domain} data-testid={`admin-seo-competitor-${i}`}>
                    <td style={TdStyle}>
                      <b>{c.name}</b><br />
                      <a href={`https://${c.domain}`} target="_blank" rel="noreferrer" style={{ color: "var(--brass-deep)", fontSize: ".76rem" }}>{c.domain} ↗</a>
                    </td>
                    <td style={{ ...TdStyle, maxWidth: 240 }}>{c.primary_positioning}</td>
                    <td style={TdStyle}>
                      {(c.content_types_present || []).map((t) => (
                        <span key={t} style={pillStyle("#556e40")}>{t}</span>
                      ))}
                      {(c.content_types_absent || []).map((t) => (
                        <span key={t} style={pillStyle("#a44")}>−{t}</span>
                      ))}
                    </td>
                    <td style={TdStyle}>{(c.topical_strength || []).join(", ")}</td>
                    <td style={{ ...TdStyle, maxWidth: 260, fontSize: ".82rem" }}>{(c.topical_gaps_vs_us || []).join("; ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Recommendations */}
          <div style={{ border: "1px solid var(--line)", padding: "1.2rem 1.4rem", background: "var(--paper-raised)" }} data-testid="admin-seo-recommendations">
            <p className="mono" style={{ fontSize: ".68rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .8rem" }}>
              SEO positioning recommendations
            </p>
            <ul style={{ margin: 0, paddingLeft: "1.2rem", color: "var(--ink)", lineHeight: 1.6 }}>
              {(data.gap_analysis_summary?.seo_positioning_recommendations || []).map((r, i) => (
                <li key={i} style={{ marginBottom: ".4rem" }}>{r}</li>
              ))}
            </ul>
          </div>
        </>
      )}
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Authority Assets tab — internal backlink STRATEGY only.
// No outreach performed. No external accounts created.
// No links purchased. No backlink data fabricated.
// ─────────────────────────────────────────────────────────────
const PRIORITY_PILL = { high: "#a44", medium: "#a97a2a", "long-term": "#6b7280" };
const STATUS_PILL = { Published: "#5a7a45", Planned: "#8a7040", Draft: "#6b7280" };

const AuthorityTab = () => {
  const summary = useMemo(() => {
    const total = AUTHORITY_ASSETS.length;
    const published = AUTHORITY_ASSETS.filter((a) => a.status === "Published").length;
    const planned = AUTHORITY_ASSETS.filter((a) => a.status === "Planned").length;
    const high = AUTHORITY_ASSETS.filter((a) => a.priority === "high").length;
    return { total, published, planned, high };
  }, []);

  return (
    <section style={{ padding: "1.6rem" }} data-testid="admin-seo-authority-tab">
      <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", marginBottom: ".8rem" }}>
        Authority assets · backlink strategy · strategy only
      </p>
      <h2 style={{ fontFamily: "var(--display)", fontSize: "1.35rem", margin: "0 0 .4rem", letterSpacing: "-.005em" }}>
        Linkable-asset library and long-term authority roadmap.
      </h2>
      <p style={{ color: "var(--ink-soft)", maxWidth: "70ch", marginBottom: "1.4rem" }}>
        This dashboard tracks Asia Sourcing content assets that can naturally earn editorial mentions, citations and industry authority over time. It performs no outreach, creates no external accounts, purchases no links, and never fabricates backlink data. Every recommendation is informational.
      </p>

      {/* Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: ".8rem", marginBottom: "1.6rem" }}>
        <SummaryTile label="Assets planned" value={summary.total} />
        <SummaryTile label="Assets published" value={summary.published} accent="#5a7a45" />
        <SummaryTile label="High-priority" value={summary.high} accent="#a44" />
        <SummaryTile label="Long-term (planned)" value={summary.planned} accent="#8a7040" />
      </div>

      {/* Assets table */}
      <div style={{ overflowX: "auto", border: "1px solid var(--line)", marginBottom: "2rem" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }} data-testid="admin-seo-authority-table">
          <thead>
            <tr>
              <th style={ThStyle}>Asset</th>
              <th style={ThStyle}>Audience</th>
              <th style={ThStyle}>Intent · theme</th>
              <th style={ThStyle}>Content type</th>
              <th style={ThStyle}>Potential sources</th>
              <th style={ThStyle}>Priority</th>
              <th style={ThStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {AUTHORITY_ASSETS.map((a, i) => (
              <tr key={i} data-testid={`admin-seo-authority-row-${i}`}>
                <td style={TdStyle}>
                  <div style={{ fontWeight: 500 }}>{a.name}</div>
                  {a.url && (
                    <Link to={a.url} style={{ ...tinyMonoStyle, color: "var(--brass-deep)", textDecoration: "none", fontSize: ".65rem" }}>
                      {a.url}
                    </Link>
                  )}
                </td>
                <td style={TdStyle}>{a.audience}</td>
                <td style={TdStyle}>
                  <div style={{ color: "var(--ink-soft)", fontSize: ".78rem" }}>{a.intent}</div>
                  <div>{a.theme}</div>
                </td>
                <td style={TdStyle}>
                  <div>{a.contentType}</div>
                  <div style={{ color: "var(--ink-soft)", fontSize: ".76rem" }}>{a.length}</div>
                </td>
                <td style={TdStyle}>
                  {a.potentialSources.map((s, j) => (
                    <span key={j} style={pillStyle("#6b7280")}>{s}</span>
                  ))}
                </td>
                <td style={TdStyle}>
                  <span style={pillStyle(PRIORITY_PILL[a.priority] || "#6b7280")}>{a.priority}</span>
                </td>
                <td style={TdStyle}>
                  <span style={pillStyle(STATUS_PILL[a.status] || "#6b7280")}>{a.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Link quality framework */}
      <h3 style={{ fontFamily: "var(--display)", fontSize: "1.1rem", margin: "0 0 .6rem" }}>Link quality framework</h3>
      <p style={{ color: "var(--ink-soft)", marginBottom: ".8rem", fontSize: ".9rem" }}>
        Score each future opportunity as Excellent · Good · Review Carefully · Avoid. Do NOT rely only on Domain Authority / Domain Rating.
      </p>
      <div style={{ overflowX: "auto", border: "1px solid var(--line)", marginBottom: "2rem" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }}>
          <thead>
            <tr>
              <th style={ThStyle}>Criterion</th>
              <th style={ThStyle}>Weight</th>
              <th style={ThStyle}>How to evaluate</th>
            </tr>
          </thead>
          <tbody>
            {LINK_QUALITY_FRAMEWORK.map((r, i) => (
              <tr key={i}>
                <td style={{ ...TdStyle, fontWeight: 500 }}>{r.criterion}</td>
                <td style={TdStyle}><span style={pillStyle(r.weight === "High" ? "#a44" : "#a97a2a")}>{r.weight}</span></td>
                <td style={TdStyle}>{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Anchor text strategy */}
      <h3 style={{ fontFamily: "var(--display)", fontSize: "1.1rem", margin: "0 0 .6rem" }}>Anchor text strategy</h3>
      <p style={{ color: "var(--ink-soft)", marginBottom: ".8rem", fontSize: ".9rem" }}>
        Anchor text should be earned naturally through editorial context, not manufactured for ranking manipulation.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: ".8rem", marginBottom: "2rem" }}>
        {Object.entries(ANCHOR_TEXT_STRATEGY).map(([type, items]) => (
          <div key={type} style={{ border: "1px solid var(--line)", padding: "1rem", background: type === "avoid" ? "rgba(170,68,68,.05)" : "var(--paper)" }}>
            <p className="mono" style={{ color: type === "avoid" ? "#a44" : "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", margin: "0 0 .5rem" }}>{type}</p>
            <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "var(--ink)", fontSize: ".85rem" }}>
              {items.map((v, i) => <li key={i} style={{ marginBottom: ".2rem" }}>{v}</li>)}
            </ul>
          </div>
        ))}
      </div>

      {/* Outreach categories */}
      <h3 style={{ fontFamily: "var(--display)", fontSize: "1.1rem", margin: "0 0 .6rem" }}>Potential authority sources</h3>
      <p style={{ color: "var(--ink-soft)", marginBottom: ".8rem", fontSize: ".9rem" }}>
        Categories only. No outreach has been performed by this dashboard. Manual curation required before any engagement.
      </p>
      <div style={{ overflowX: "auto", border: "1px solid var(--line)", marginBottom: "2rem" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }}>
          <thead>
            <tr>
              <th style={ThStyle}>Category</th>
              <th style={ThStyle}>Why it matters</th>
              <th style={ThStyle}>Ideal relationship approach</th>
            </tr>
          </thead>
          <tbody>
            {OUTREACH_CATEGORIES.map((c, i) => (
              <tr key={i}>
                <td style={{ ...TdStyle, fontWeight: 500 }}>{c.name}</td>
                <td style={TdStyle}>{c.why}</td>
                <td style={TdStyle}>{c.how}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Competitor authority gap placeholder */}
      <h3 style={{ fontFamily: "var(--display)", fontSize: "1.1rem", margin: "0 0 .6rem" }}>Competitor authority gap</h3>
      <div style={{ border: "1px solid var(--line)", padding: "1.4rem", background: "var(--paper-raised)", marginBottom: "2rem" }}>
        <p style={{ color: "var(--ink-soft)", margin: 0, fontSize: ".9rem" }}>
          No competitor backlink data connected yet. This section will populate when a verified backlink data source (Ahrefs / Majestic / Search Console link report) is wired in. Do NOT fabricate competitor data.
        </p>
      </div>

      {/* Roadmap */}
      <h3 style={{ fontFamily: "var(--display)", fontSize: "1.1rem", margin: "0 0 .6rem" }}>Authority roadmap</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: ".8rem", marginBottom: "2rem" }}>
        {AUTHORITY_ROADMAP.map((r, i) => (
          <div key={i} style={{ border: "1px solid var(--line)", padding: "1rem", background: "var(--paper)" }}>
            <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", margin: "0 0 .5rem" }}>{r.phase}</p>
            <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: ".85rem", color: "var(--ink)" }}>
              {r.items.map((it, j) => <li key={j} style={{ marginBottom: ".25rem" }}>{it}</li>)}
            </ul>
          </div>
        ))}
      </div>

      {/* Forbidden methods */}
      <h3 style={{ fontFamily: "var(--display)", fontSize: "1.1rem", margin: "0 0 .6rem", color: "#a44" }}>Forbidden link-building methods</h3>
      <div style={{ border: "1px solid rgba(170,68,68,.3)", background: "rgba(170,68,68,.05)", padding: "1rem 1.4rem", marginBottom: "1.6rem" }}>
        <ul style={{ margin: 0, paddingLeft: "1.1rem", columns: 2, columnGap: "2rem", color: "var(--ink)", fontSize: ".88rem" }}>
          {FORBIDDEN_METHODS.map((m, i) => <li key={i} style={{ marginBottom: ".25rem" }}>{m}</li>)}
        </ul>
        <p style={{ margin: "1rem 0 0", color: "var(--ink-soft)", fontSize: ".85rem" }}>
          Asia Sourcing should prioritize genuine industry recognition, useful content, expert participation and editorial mentions.
        </p>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Style helpers
// ─────────────────────────────────────────────────────────────
const SummaryTile = ({ label, value, accent }) => (
  <div style={{ border: "1px solid var(--line)", padding: "1rem 1.1rem", background: "var(--paper)" }}>
    <p className="mono" style={{ color: accent || "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", margin: "0 0 .3rem" }}>{label}</p>
    <p style={{ fontFamily: "var(--display)", fontSize: "1.5rem", margin: 0, letterSpacing: "-.005em" }}>{value}</p>
  </div>
);

const ThStyle = { textAlign: "left", padding: ".7rem .9rem", background: "var(--paper-raised)", borderBottom: "1px solid var(--line)", color: "var(--brass-deep)", fontFamily: "var(--mono)", fontSize: ".62rem", letterSpacing: ".1em", textTransform: "uppercase", whiteSpace: "nowrap" };
const TdStyle = { padding: ".65rem .9rem", borderBottom: "1px solid var(--line)", verticalAlign: "top", color: "var(--ink)" };
const tinyMonoStyle = { fontSize: ".72rem", letterSpacing: ".08em", textTransform: "uppercase" };
const filterSelectStyle = { background: "var(--paper)", border: "1px solid var(--line)", padding: ".55rem .8rem", fontFamily: "var(--mono)", fontSize: ".78rem", color: "var(--ink)", textTransform: "lowercase" };
const inputStyle = { flex: "1 1 240px", minWidth: "200px", background: "var(--paper)", border: "1px solid var(--line)", padding: ".55rem .85rem", fontFamily: "var(--display)", fontSize: ".95rem", color: "var(--ink)" };
const errBoxStyle = { color: "#a44", padding: ".8rem 1rem", border: "1px solid rgba(170,68,68,.3)", background: "rgba(170,68,68,.06)" };
const codeStyle = { fontFamily: "var(--mono)", background: "var(--paper-raised)", padding: ".15em .4em", fontSize: ".82em", border: "1px solid var(--line)" };
const metricTd = (hasValue) => ({ ...TdStyle, color: hasValue ? "var(--ink)" : "var(--ink-soft)", fontStyle: hasValue ? "normal" : "italic", fontVariantNumeric: "tabular-nums" });
const pillStyle = (color) => ({ display: "inline-block", padding: ".15em .5em", margin: ".15em .2em .15em 0", fontFamily: "var(--mono)", fontSize: ".62rem", letterSpacing: ".05em", textTransform: "uppercase", color, border: `1px solid ${color}`, borderRadius: 2 });

const ThSortable = ({ label, k, sortKey, sortDir, onClick }) => (
  <th style={{ ...ThStyle, cursor: "pointer", userSelect: "none" }} onClick={() => onClick(k)} data-testid={`admin-seo-th-${k}`}>
    {label}
    {sortKey === k && <span style={{ marginLeft: ".4em" }}>{sortDir === "asc" ? "↑" : "↓"}</span>}
  </th>
);

// ─────────────────────────────────────────────────────────────
// Sprint 14 · Page drafts tab (isolated staging layer — never touches served SEO JSON)
// ─────────────────────────────────────────────────────────────

// Non-exhaustive curated list of SEO routes with editable hero. Admin can also
// enter any slug manually via the free-form input. Servers/data files stay untouched.
const SUGGESTED_SLUGS = [
  "/", "/usa", "/regions/uk", "/regions/germany", "/regions/france", "/regions/netherlands",
  "/regions/sweden", "/regions/finland",
  "/regions/uk/furniture-sourcing", "/regions/uk/lighting-sourcing", "/regions/uk/home-decor-sourcing",
  "/regions/germany/furniture-sourcing", "/regions/germany/lighting-sourcing",
  "/regions/france/furniture-sourcing", "/regions/france/lighting-sourcing",
  "/regions/sweden/furniture-sourcing", "/regions/sweden/lighting-sourcing",
  "/regions/finland/furniture-sourcing", "/regions/finland/lighting-sourcing",
  "/services/furniture-sourcing-company-india", "/services/furniture-quality-control-india",
  "/services/table-lamp-sourcing-india", "/services/wall-light-sourcing-india",
  "/services/floor-lamp-sourcing-india", "/services/chandelier-sourcing-india",
  "/trade-fairs/ihgf-delhi", "/trade-fairs/imm-cologne", "/trade-fairs/maison-et-objet",
  "/trade-fairs/high-point-market", "/trade-fairs/las-vegas-market",
  "/categories/furniture", "/categories/lighting", "/categories/home-decor",
  "/clusters/jodhpur", "/clusters/moradabad", "/clusters/saharanpur", "/clusters/firozabad",
];

// SEO-friendly filename derived from slug when the admin has not set one.
const seoFilenameFrom = (slug) => {
  const base = (slug || "/").replace(/^\/+|\/+$/g, "").replace(/[^a-z0-9/-]+/gi, "-").replace(/\/+/g, "-") || "home";
  return `${base}-hero.png`.toLowerCase();
};

const PageDraftsTab = () => {
  const [drafts, setDrafts] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [manualSlug, setManualSlug] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // "hero" | "og" | null
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [chosenPrompt, setChosenPrompt] = useState("");

  const loadList = async () => {
    try {
      const r = await fetch(`${API}/api/admin/seo/page-drafts`, { credentials: "include" });
      if (r.ok) setDrafts(await r.json());
    } catch { /* silent */ }
  };
  useEffect(() => { loadList(); }, []);

  const loadDraft = async (slug) => {
    if (!slug) return;
    setLoading(true); setError(""); setSaved(false);
    try {
      const r = await fetch(`${API}/api/admin/seo/page-drafts/by-slug?slug=${encodeURIComponent(slug)}`, { credentials: "include" });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      const d = await r.json();
      setDraft({
        slug,
        hero_image_id: d.hero_image_id || null,
        hero_image_url: d.hero_image_id ? `${API}/api/ai-studio/files/${d.hero_image_id}` : null,
        hero_alt: d.hero_alt || "",
        hero_caption: d.hero_caption || "",
        og_image: d.og_image || null,
        og_image_url: d.og_image ? `${API}/api/ai-studio/files/${d.og_image}` : null,
        seo_filename: d.seo_filename || seoFilenameFrom(slug),
        updated_at: d.updated_at,
        updated_by: d.updated_by,
      });
    } catch (e) { setError(String(e.message || e)); setDraft(null); }
    finally { setLoading(false); }
  };

  const openSlug = (slug) => {
    setSelectedSlug(slug);
    setChosenPrompt("");
    setSuggestedPrompts([]);
    loadDraft(slug);
    // Fetch 3 on-brand prompt suggestions for this slug (fire-and-forget).
    fetch(`${API}/api/admin/seo/page-drafts/prompt-suggestions?slug=${encodeURIComponent(slug)}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && Array.isArray(d.prompts)) setSuggestedPrompts(d.prompts); })
      .catch(() => {});
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true); setError(""); setSaved(false);
    try {
      const payload = {
        hero_image_id: draft.hero_image_id,
        hero_alt: draft.hero_alt || null,
        hero_caption: draft.hero_caption || null,
        og_image: draft.og_image,
        seo_filename: draft.seo_filename || seoFilenameFrom(draft.slug),
      };
      const r = await fetch(`${API}/api/admin/seo/page-drafts/by-slug?slug=${encodeURIComponent(draft.slug)}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: `HTTP ${r.status}` }));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      setSaved(true);
      await loadList();
    } catch (e) { setError(String(e.message || e)); }
    finally { setSaving(false); }
  };

  const deleteDraft = async () => {
    if (!draft) return;
    if (!window.confirm(`Delete draft for ${draft.slug}?`)) return;
    setSaving(true);
    try {
      await fetch(`${API}/api/admin/seo/page-drafts/by-slug?slug=${encodeURIComponent(draft.slug)}`, {
        method: "DELETE", credentials: "include",
      });
      setDraft(null); setSelectedSlug("");
      await loadList();
    } finally { setSaving(false); }
  };

  const handlePick = (img) => {
    if (!pickerFor) return;
    if (pickerFor === "hero") {
      setDraft((d) => ({
        ...d,
        hero_image_id: img.id,
        hero_image_url: `${API}${img.url}`,
        hero_alt: d?.hero_alt || (img.prompt?.slice(0, 200) || ""),
      }));
    } else if (pickerFor === "og") {
      setDraft((d) => ({ ...d, og_image: img.id, og_image_url: `${API}${img.url}` }));
    }
    setPickerFor(null);
  };

  return (
    <section style={{ padding: "1.6rem", display: "grid", gridTemplateColumns: "300px 1fr", gap: "1.4rem" }} data-testid="admin-seo-drafts-tab">
      {/* Left · slug list */}
      <aside>
        <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .5rem" }}>Existing drafts ({drafts.length})</p>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, marginBottom: "1rem", maxHeight: 260, overflowY: "auto", border: "1px solid var(--line)" }}>
          {drafts.length === 0 && <li style={{ padding: ".7rem .9rem", color: "var(--ink-soft)", fontStyle: "italic" }}>No drafts yet.</li>}
          {drafts.map((d) => (
            <li key={d.slug}>
              <button type="button" onClick={() => openSlug(d.slug)} data-testid={`admin-seo-draft-${d.slug.replace(/\W+/g, "-")}`} style={{
                width: "100%", textAlign: "left", padding: ".55rem .8rem", background: selectedSlug === d.slug ? "var(--paper-raised)" : "transparent",
                border: 0, borderBottom: "1px solid var(--line)", cursor: "pointer", fontFamily: "inherit", fontSize: ".85rem",
              }}>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.slug}</div>
                <div className="mono" style={{ fontSize: ".62rem", color: "var(--ink-soft)", letterSpacing: ".05em", marginTop: ".15rem" }}>{new Date(d.updated_at).toLocaleDateString()}</div>
              </button>
            </li>
          ))}
        </ul>

        <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .5rem" }}>Suggested slugs</p>
        <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid var(--line)", padding: ".3rem 0" }}>
          {SUGGESTED_SLUGS.map((s) => (
            <button key={s} type="button" onClick={() => openSlug(s)} data-testid={`admin-seo-suggest-${s.replace(/\W+/g, "-")}`} style={{
              display: "block", width: "100%", textAlign: "left", padding: ".35rem .8rem", background: selectedSlug === s ? "var(--paper-raised)" : "transparent",
              border: 0, cursor: "pointer", fontFamily: "var(--mono)", fontSize: ".72rem", letterSpacing: ".04em", color: "var(--ink)",
            }}>{s}</button>
          ))}
        </div>

        <div style={{ marginTop: ".8rem", display: "flex", gap: ".4rem" }}>
          <input type="text" value={manualSlug} onChange={(e) => setManualSlug(e.target.value)} placeholder="/other/slug" data-testid="admin-seo-draft-slug-input" style={{ ...inputStyle, flex: 1 }} />
          <button type="button" onClick={() => manualSlug && openSlug(manualSlug.trim())} data-testid="admin-seo-draft-slug-load" className="btn btn-ghost" style={{ padding: ".4rem .8rem", fontSize: ".8rem" }}>Load</button>
        </div>
      </aside>

      {/* Right · editor */}
      <div>
        {!draft && !loading && (
          <div style={{ padding: "2rem", border: "1px dashed var(--line)", color: "var(--ink-soft)", fontStyle: "italic" }}>
            Select a slug on the left to start editing its hero-image draft.
          </div>
        )}
        {loading && <p className="mono" style={{ color: "var(--brass-deep)" }}>Loading draft…</p>}
        {draft && (
          <div data-testid="admin-seo-draft-editor">
            <div style={{ marginBottom: ".8rem" }}>
              <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: 0 }}>Editing draft (staging only — production JSON is not modified)</p>
              <h3 style={{ fontFamily: "var(--display)", fontSize: "1.3rem", margin: ".2rem 0 0" }}>{draft.slug}</h3>
              {draft.updated_at && (
                <p style={{ margin: ".2rem 0 0", fontSize: ".8rem", color: "var(--ink-soft)" }}>Last updated {new Date(draft.updated_at).toLocaleString()} by {draft.updated_by}</p>
              )}
            </div>

            {/* Hero image */}
            <div style={{ border: "1px solid var(--line)", padding: "1rem 1.2rem", background: "var(--paper-raised)", marginBottom: "1rem" }}>
              <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .6rem" }}>Hero / featured image</p>
              {draft.hero_image_url ? (
                <div>
                  <img src={draft.hero_image_url} alt={draft.hero_alt || "Hero"} data-testid="admin-seo-draft-hero-preview" style={{ maxWidth: "100%", maxHeight: 320, border: "1px solid var(--line)", display: "block", marginBottom: ".6rem" }} />
                  <div style={{ display: "flex", gap: ".4rem", marginBottom: ".8rem" }}>
                    <button type="button" onClick={() => setPickerFor("hero")} className="btn btn-ghost" data-testid="admin-seo-draft-hero-replace" style={{ padding: ".3rem .8rem", fontSize: ".8rem" }}>Replace</button>
                    <button type="button" onClick={() => setDraft((d) => ({ ...d, hero_image_id: null, hero_image_url: null }))} className="btn btn-ghost" data-testid="admin-seo-draft-hero-remove" style={{ padding: ".3rem .8rem", fontSize: ".8rem" }}>Remove</button>
                  </div>
                </div>
              ) : (
                <>
                  {suggestedPrompts.length > 0 && (
                    <div style={{ marginBottom: ".8rem" }} data-testid="admin-seo-draft-prompt-suggestions">
                      <p className="mono" style={{ fontSize: ".6rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .5rem" }}>
                        On-brand prompts for this page · tap to generate
                      </p>
                      <div style={{ display: "grid", gap: ".5rem" }}>
                        {suggestedPrompts.map((p, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => { setChosenPrompt(p); setPickerFor("hero"); }}
                            data-testid={`admin-seo-draft-prompt-${i}`}
                            style={{
                              textAlign: "left",
                              padding: ".55rem .75rem",
                              background: "var(--paper)",
                              border: "1px solid var(--line)",
                              borderLeft: "3px solid var(--brass-deep)",
                              fontFamily: "inherit",
                              fontSize: ".8rem",
                              color: "var(--ink)",
                              lineHeight: 1.5,
                              cursor: "pointer",
                              transition: "background .15s ease",
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.background = "var(--paper-raised)")}
                            onMouseOut={(e) => (e.currentTarget.style.background = "var(--paper)")}
                          >
                            <span className="mono" style={{ fontSize: ".6rem", letterSpacing: ".1em", color: "var(--brass-deep)", textTransform: "uppercase", marginRight: ".4rem" }}>Prompt {i + 1} →</span>
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <button type="button" onClick={() => { setChosenPrompt(""); setPickerFor("hero"); }} className="btn btn-primary" data-testid="admin-seo-draft-hero-generate" style={{ padding: ".5rem 1rem", fontSize: ".9rem", marginBottom: ".8rem" }}>Open image picker →</button>
                </>
              )}
              <label style={{ display: "block", marginBottom: ".6rem" }}>
                <p className="mono" style={{ fontSize: ".6rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .25rem" }}>Alt text (SEO + accessibility)</p>
                <input type="text" value={draft.hero_alt} onChange={(e) => setDraft((d) => ({ ...d, hero_alt: e.target.value }))} data-testid="admin-seo-draft-hero-alt" style={inputStyle} maxLength={240} />
              </label>
              <label style={{ display: "block", marginBottom: ".6rem" }}>
                <p className="mono" style={{ fontSize: ".6rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .25rem" }}>Caption</p>
                <input type="text" value={draft.hero_caption} onChange={(e) => setDraft((d) => ({ ...d, hero_caption: e.target.value }))} data-testid="admin-seo-draft-hero-caption" style={inputStyle} maxLength={400} />
              </label>
              <label style={{ display: "block" }}>
                <p className="mono" style={{ fontSize: ".6rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .25rem" }}>SEO filename (used when downloading / re-uploading to CDN)</p>
                <input type="text" value={draft.seo_filename} onChange={(e) => setDraft((d) => ({ ...d, seo_filename: e.target.value }))} data-testid="admin-seo-draft-hero-filename" style={inputStyle} maxLength={120} />
              </label>
            </div>

            {/* OG image */}
            <div style={{ border: "1px solid var(--line)", padding: "1rem 1.2rem", background: "var(--paper-raised)", marginBottom: "1rem" }}>
              <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .6rem" }}>Open Graph / social image (optional)</p>
              {draft.og_image_url ? (
                <>
                  <img src={draft.og_image_url} alt="OG" data-testid="admin-seo-draft-og-preview" style={{ maxWidth: 300, maxHeight: 160, border: "1px solid var(--line)", display: "block", marginBottom: ".6rem" }} />
                  <div style={{ display: "flex", gap: ".4rem" }}>
                    <button type="button" onClick={() => setPickerFor("og")} className="btn btn-ghost" data-testid="admin-seo-draft-og-replace" style={{ padding: ".3rem .8rem", fontSize: ".8rem" }}>Replace</button>
                    <button type="button" onClick={() => setDraft((d) => ({ ...d, og_image: null, og_image_url: null }))} className="btn btn-ghost" data-testid="admin-seo-draft-og-remove" style={{ padding: ".3rem .8rem", fontSize: ".8rem" }}>Remove</button>
                  </div>
                </>
              ) : (
                <button type="button" onClick={() => setPickerFor("og")} className="btn btn-ghost" data-testid="admin-seo-draft-og-generate" style={{ padding: ".4rem 1rem", fontSize: ".85rem" }}>Add OG image →</button>
              )}
            </div>

            {/* JSON-LD image field preview (informational only) */}
            {draft.hero_image_id && (
              <details style={{ marginBottom: "1rem" }}>
                <summary className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", cursor: "pointer" }}>Metadata preview (informational)</summary>
                <pre style={{ background: "var(--paper-raised)", border: "1px solid var(--line)", padding: "1rem", fontSize: ".78rem", overflow: "auto" }}>{JSON.stringify({
                  slug: draft.slug,
                  image: {
                    url: `${API}/api/media/${draft.hero_image_id}`,
                    alt: draft.hero_alt,
                    caption: draft.hero_caption,
                    filename: draft.seo_filename,
                  },
                  open_graph: draft.og_image ? { image: `${API}/api/media/${draft.og_image}` } : null,
                  schema_image_field: `${API}/api/media/${draft.hero_image_id}`,
                }, null, 2)}</pre>
              </details>
            )}

            {error && <p data-testid="admin-seo-draft-error" className="mono" style={{ color: "#a44", fontSize: ".85rem", marginBottom: ".6rem" }}>{error}</p>}
            {saved && <p data-testid="admin-seo-draft-saved" className="mono" style={{ color: "#3a7", fontSize: ".85rem", marginBottom: ".6rem" }}>Draft saved · production SEO JSON is untouched.</p>}

            <div style={{ display: "flex", gap: ".6rem" }}>
              <button type="button" onClick={save} disabled={saving} className="btn btn-primary" data-testid="admin-seo-draft-save" style={{ padding: ".55rem 1.2rem", fontSize: ".9rem" }}>{saving ? "Saving…" : "Save draft"}</button>
              <button type="button" onClick={deleteDraft} disabled={saving} className="btn btn-ghost" data-testid="admin-seo-draft-delete" style={{ padding: ".55rem 1.2rem", fontSize: ".9rem" }}>Delete draft</button>
            </div>
          </div>
        )}
      </div>

      {pickerFor && (
        <AiImagePicker
          contextLabel={pickerFor === "hero" ? "SEO hero" : "SEO OG image"}
          onClose={() => setPickerFor(null)}
          onSelect={handlePick}
          defaultPrompt={chosenPrompt || `Hero image for ${draft?.slug || "SEO page"} — quiet-luxury B2B India sourcing aesthetic, natural morning light, no text overlays.`}
        />
      )}
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Sprint 14 · Resend status tab
// ─────────────────────────────────────────────────────────────
const ResendStatusTab = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/admin/resend/status`, { credentials: "include" });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      setStatus(await r.json());
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const triggerVerify = async () => {
    setVerifying(true); setError(""); setTestResult(null);
    try {
      const r = await fetch(`${API}/api/admin/resend/verify`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      setStatus(await r.json());
    } catch (e) { setError(String(e.message || e)); }
    finally { setVerifying(false); }
  };

  const sendTest = async () => {
    setTesting(true); setError(""); setTestResult(null);
    try {
      const r = await fetch(`${API}/api/admin/resend/test-email`, { method: "POST", credentials: "include" });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `${r.status} ${r.statusText}`);
      setTestResult(body);
    } catch (e) { setError(String(e.message || e)); }
    finally { setTesting(false); }
  };

  const copyValue = async (v, key) => {
    try {
      await navigator.clipboard.writeText(v);
      setCopied(key);
      setTimeout(() => setCopied(""), 1600);
    } catch (_) {}
  };

  const recordRowBg = (st) => {
    if (st === "verified") return "rgba(83,110,64,.10)";
    if (st === "failed") return "rgba(190,40,40,.07)";
    if (st === "pending") return "rgba(197,168,128,.10)";
    return "transparent";
  };

  return (
    <section style={{ padding: "2rem 1.6rem", maxWidth: 980 }} data-testid="admin-seo-resend-tab">
      <h2 className="display" style={{ fontSize: "1.4rem", margin: "0 0 .6rem", letterSpacing: "-.01em" }}>Resend email status</h2>
      <p style={{ color: "var(--ink-soft)", marginBottom: "1.4rem", lineHeight: 1.5 }}>
        Sender configuration + real-time domain verification state pulled directly from Resend's Domains API. This dashboard never falsely reports a domain as verified.
      </p>
      {loading && <p className="mono" style={{ color: "var(--brass-deep)" }}>Loading Resend status…</p>}
      {error && <p className="mono" style={errBoxStyle}>{error}</p>}
      {status && (
        <>
          <div
            style={{
              border: "1px solid var(--line)", padding: "1.2rem 1.4rem",
              background: status.domain_verified ? "rgba(83,110,64,.08)" : "rgba(197,168,128,.08)",
              marginBottom: "1.4rem",
            }}
            data-testid="admin-seo-resend-status-card"
          >
            <p className="mono" style={{ fontSize: ".7rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .5rem" }}>
              Domain · {status.target_domain} · {status.domain_verified ? "verified" : status.domain_status.replace(/_/g, " ")}
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: ".3rem 1rem", fontSize: ".9rem", color: "var(--ink)" }}>
              <div className="mono" style={{ color: "var(--ink-soft)", fontSize: ".78rem" }}>Sender email</div>
              <div><b>{status.sender_email}</b></div>
              <div className="mono" style={{ color: "var(--ink-soft)", fontSize: ".78rem" }}>Notification email</div>
              <div>{status.notification_email || "—"}</div>
              <div className="mono" style={{ color: "var(--ink-soft)", fontSize: ".78rem" }}>Resend configured</div>
              <div>{status.resend_configured ? "yes" : "no"}</div>
              <div className="mono" style={{ color: "var(--ink-soft)", fontSize: ".78rem" }}>Domain verified</div>
              <div style={{ color: status.domain_verified ? "#3a7" : "#a97a2a", fontWeight: 600 }} data-testid="admin-seo-resend-verified">{status.domain_verified ? "YES" : "NO"}</div>
              <div className="mono" style={{ color: "var(--ink-soft)", fontSize: ".78rem" }}>Domain status</div>
              <div>{status.domain_status}</div>
            </div>
            {status.note && (
              <p style={{ margin: "1rem 0 0", padding: ".7rem .9rem", background: "rgba(197,168,128,.15)", border: "1px solid rgba(169,122,42,.4)", fontSize: ".85rem", color: "#5A4020" }}>
                {status.note}
              </p>
            )}
          </div>

          {!status.domain_verified && status.dns_records && status.dns_records.length > 0 && (
            <div style={{ border: "1px solid var(--line)", padding: "1rem 1.2rem", background: "var(--paper-raised)", marginBottom: "1.4rem" }} data-testid="admin-seo-resend-dns-records">
              <p className="mono" style={{ fontSize: ".68rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .5rem" }}>DNS records to publish at your registrar</p>
              <p style={{ margin: "0 0 .9rem", fontSize: ".88rem", color: "var(--ink)", lineHeight: 1.55 }}>
                Add each row below to the DNS zone for <code style={codeStyle}>{status.target_domain}</code>. The record <b>name</b> is relative (your registrar typically appends the domain automatically — do not add it twice).
              </p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".82rem" }}>
                  <thead>
                    <tr>
                      <th style={ThStyle}>Type</th>
                      <th style={ThStyle}>Name</th>
                      <th style={ThStyle}>Value</th>
                      <th style={ThStyle}>Priority</th>
                      <th style={ThStyle}>TTL</th>
                      <th style={ThStyle}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status.dns_records.map((r, i) => (
                      <tr key={i} style={{ background: recordRowBg(r.status) }} data-testid={`admin-seo-resend-dns-row-${i}`}>
                        <td style={TdStyle}><b>{r.type}</b></td>
                        <td style={TdStyle}><code style={codeStyle}>{r.name}</code></td>
                        <td style={{ ...TdStyle, maxWidth: 360, wordBreak: "break-all" }}>
                          <div style={{ display: "flex", gap: ".4rem", alignItems: "flex-start" }}>
                            <code style={{ ...codeStyle, flex: 1 }}>{r.value}</code>
                            <button
                              type="button"
                              onClick={() => copyValue(r.value, `v-${i}`)}
                              className="btn btn-ghost"
                              style={{ padding: ".2rem .55rem", fontSize: ".7rem", flexShrink: 0 }}
                              data-testid={`admin-seo-resend-copy-${i}`}
                            >
                              {copied === `v-${i}` ? "copied" : "copy"}
                            </button>
                          </div>
                        </td>
                        <td style={TdStyle}>{r.priority ?? "—"}</td>
                        <td style={TdStyle}>{r.ttl}</td>
                        <td style={{ ...TdStyle, color: r.status === "verified" ? "#3a7" : r.status === "failed" ? "#b23" : "#a97a2a", fontWeight: 600 }}>{r.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ol style={{ margin: "1.1rem 0 0", paddingLeft: "1.2rem", color: "var(--ink)", lineHeight: 1.6, fontSize: ".9rem" }}>
                <li>Paste these records into your registrar (Cloudflare / GoDaddy / Route53 / etc.). Propagation typically takes 5-30 min.</li>
                <li>Click <b>Trigger reverify</b> below — Resend will re-query DNS and update the status.</li>
                <li>Once <b>Domain verified · YES</b>, update <code style={codeStyle}>SENDER_EMAIL</code> in <code style={codeStyle}>/app/backend/.env</code> to <code style={codeStyle}>noreply@{status.target_domain}</code> and restart the backend.</li>
                <li>Click <b>Send test email</b> to confirm end-to-end delivery.</li>
              </ol>
            </div>
          )}

          <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginBottom: "1rem" }}>
            <button type="button" onClick={load} className="btn btn-ghost" data-testid="admin-seo-resend-refresh" style={{ padding: ".5rem 1.1rem", fontSize: ".85rem" }}>Refresh status</button>
            <button type="button" onClick={triggerVerify} disabled={verifying} className="btn btn-fill" data-testid="admin-seo-resend-verify-btn" style={{ padding: ".5rem 1.1rem", fontSize: ".85rem", opacity: verifying ? .6 : 1 }}>
              {verifying ? "Reverifying…" : "Trigger reverify"}
            </button>
            <button type="button" onClick={sendTest} disabled={testing} className="btn btn-ghost" data-testid="admin-seo-resend-test-btn" style={{ padding: ".5rem 1.1rem", fontSize: ".85rem", opacity: testing ? .6 : 1 }}>
              {testing ? "Sending…" : "Send test email"}
            </button>
          </div>
          {testResult && (
            <p className="mono" style={{ padding: ".6rem .9rem", background: "rgba(83,110,64,.1)", border: "1px solid rgba(83,110,64,.3)", fontSize: ".82rem", color: "#2a4a1f", margin: "0 0 1rem" }} data-testid="admin-seo-resend-test-result">
              ✓ Test email dispatched · Resend id {testResult.resend_id?.slice(0, 10) || "—"} · from <b>{testResult.from}</b> to <b>{testResult.to}</b>
            </p>
          )}

          {status.domains_found?.length > 0 && (
            <>
              <h3 style={{ fontFamily: "var(--display)", fontSize: "1.05rem", margin: "1.6rem 0 .6rem" }}>All domains on this Resend account</h3>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".88rem" }}>
                <thead><tr><th style={ThStyle}>Domain</th><th style={ThStyle}>Region</th><th style={ThStyle}>Status</th></tr></thead>
                <tbody>
                  {status.domains_found.map((d, i) => (
                    <tr key={i}><td style={TdStyle}>{d.name}</td><td style={TdStyle}>{d.region || "—"}</td><td style={TdStyle}>{d.status}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </section>
  );
};


export default AdminSeo;
